import { Tooltip } from "react-tooltip";
import { useNavigate } from "react-router";
import {
	CheckBadgeIcon as CheckBadgeIconOutline,
	ClockIcon,
	ExclamationTriangleIcon,
} from "@heroicons/react/24/outline";
import { CheckBadgeIcon } from "@heroicons/react/24/solid";
import { toOrdinal } from "../../../app/utils";
import ProgressBar from "../../../components/ProgressBar";
import FormattedDate from "../../../components/FormattedDate";
import Avatar from "../../../components/Avatar";
import {
	PeerReviewRequestViewModel,
	PeerReviewsActivityPerPromptViewModel,
	PeerReviewListItemStatus,
	PeerReviewStats,
	PeerReviewUserInfoViewModel,
} from "../peerReviewsApiSlice";

// Filter
export const statusOptions = [
	{ value: "All", label: "All" },
	...[{ value: "Not Started", label: "Not Started" }],
	...[{ value: "In Progress", label: "In Progress" }],
	...[{ value: "Completed", label: "Completed" }],
];

export const isAllOrEmpty = (value: string) => value === "All";

export const matchesStatus = (status: PeerReviewListItemStatus, selectedStatus: string) => {
	if (isAllOrEmpty(selectedStatus)) return true;

	switch (selectedStatus) {
		case "Not Started":
			return status === PeerReviewListItemStatus.NotStarted;
		case "In Progress":
			return status === PeerReviewListItemStatus.InProgress;
		case "Completed":
			return status === PeerReviewListItemStatus.Complete;
		default:
			return true;
	}
};

// Table
export const studentNameBodyTemplate = (writer: PeerReviewUserInfoViewModel) => {
	return (
		<div className="flex gap-2 items-center">
			<Avatar
				className="!size-8"
				hasImage={writer.hasImage}
				initials={writer.initials}
				userId={writer.id}
			/>
			<span>{writer.formalFullName}</span>
		</div>
	);
};

export const totalReviewsBodyTemplate = (rowData: PeerReviewsActivityPerPromptViewModel) => {
	return rowData.totalReviews ? rowData.totalReviews : "-";
};

export const progressBodyTemplate = (stats: PeerReviewStats, id: number) => {
	if (stats.totalReviews === 0) {
		return "-";
	}

	const allComplete = stats.completedReviews === stats.totalReviews;

	return allComplete ? (
		<div className="items-center text-green-700 inline-flex float-right">
			<CheckBadgeIcon className="mr-1.5 h-7 w-7" aria-hidden="true" />
			<p>
				<strong>
					{stats.completedReviews}
					<span className="text-xs">/{stats.totalReviews}</span>
				</strong>{" "}
				submitted!
			</p>
		</div>
	) : stats.completedReviews == 0 ? (
		<div className="w-full">
			<ProgressBar
				id={id}
				started={stats.inProgressReviews}
				showStartedOnly={true}
				assigned={stats.totalReviews}
				barColor="bg-zinc-400"
			/>
			<p className="text-gray-500 text-sm text-right pt-0.5">
				<strong>
					{stats.inProgressReviews}
					<span className="text-xs">/{stats.totalReviews}</span>
				</strong>{" "}
				reviews have started
			</p>
		</div>
	) : (
		<div className="w-full">
			<ProgressBar
				id={id}
				started={stats.inProgressReviews}
				completed={stats.completedReviews}
				assigned={stats.totalReviews}
				completedLabel={"Submitted"}
			/>
			<p className="text-gray-500 text-sm text-right pt-0.5">
				<strong>
					{stats.completedReviews}
					<span className="text-xs">/{stats.totalReviews}</span>
				</strong>{" "}
				reviews have submitted
			</p>
		</div>
	);
};

export const draftNumberBodyTemplate = (value?: number) => {
	return value ? toOrdinal(value) : "-";
};

export const statusBodyTemplate = (rowData: PeerReviewRequestViewModel) => {
	let status = "";
	switch (rowData.reviewStatus) {
		case PeerReviewListItemStatus.Complete:
			status = "Completed";
			break;
		case PeerReviewListItemStatus.InProgress:
			status = "In Progress";
			break;
		default:
			status = "Not Started";
	}

	return <span>{status}</span>;
};

export const statusIconBodyTemplate = (rowData: PeerReviewRequestViewModel) => {
	return (
		<>
			{rowData.reviewStatus === PeerReviewListItemStatus.NotStarted ? (
				<ExclamationTriangleIcon
					tabIndex={-1}
					className="size-5 text-red-500"
					aria-hidden="true"
					data-tooltip-id="not-started-tooltip"
				/>
			) : rowData.reviewStatus === PeerReviewListItemStatus.Complete ? (
				<CheckBadgeIconOutline
					tabIndex={-1}
					className="size-5 text-green-700"
					aria-hidden="true"
					data-tooltip-id="completed-tooltip"
				/>
			) : (
				<ClockIcon
					tabIndex={-1}
					className="size-5 text-yellow-500"
					aria-hidden="true"
					data-tooltip-id="in-progress-tooltip"
				/>
			)}
			<Tooltip id="not-started-tooltip" place="top" content="Not Started" />
			<Tooltip id="in-progress-tooltip" place="top" content="In Progress" />
			<Tooltip id="completed-tooltip" place="top" content="Completed" />
		</>
	);
};

export const reviewDateBodyTemplate = (rowData: PeerReviewRequestViewModel) => {
	return rowData.reviewDate ? <FormattedDate date={rowData.reviewDate} /> : "-";
};

export const actionBodyTemplate = (rowData: PeerReviewRequestViewModel) => {
	const navigate = useNavigate();
	return rowData.reviewStatus != PeerReviewListItemStatus.NotStarted ? (
		<button
			id={`essay-review-${rowData.peerReviewId}`}
			className="btn btn-action !px-2"
			onClick={() => navigate(`/reviews/${rowData.peerReviewId}`)}>
			View
		</button>
	) : (
		<></>
	);
};
